import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import * as sched from 'sched';
import { useAuth } from './useAuth';
import { WorkspaceContext } from '../context/WorkspaceContext';

// ── Types ────────────────────────────────────────────────────────────

export interface TemplateCache {
  /** Get (or fetch + cache) a template state, optionally at a specific version (event count). */
  getTemplateState(templateId: string, version?: number): Promise<sched.WorkflowTemplateState>;
  /** The Rust-side opaque cache, kept in sync. Pass this to build_database_fuzzy_schedule. */
  wrappedCache: sched.WrappedTemplateCache;
}

// ── Context ──────────────────────────────────────────────────────────

const TemplateCacheContext = createContext<TemplateCache | null>(null);

export function useTemplateCache(): TemplateCache {
  const ctx = useContext(TemplateCacheContext);
  if (!ctx) throw new Error('useTemplateCache must be used within a TemplateCacheProvider');
  return ctx;
}

// ── Provider ─────────────────────────────────────────────────────────

export function TemplateCacheProvider({ children }: { children: React.ReactNode }) {
  const { client, token } = useAuth();
  const { safeData } = useContext(WorkspaceContext);

  // JS-side cache: templateId → full event list (all versions)
  const eventsCache = useRef<Map<string, sched.WorkflowTemplateEvent[]>>(new Map());

  // In-flight fetch promises to deduplicate concurrent requests
  const inflight = useRef<Map<string, Promise<sched.WorkflowTemplateEvent[]>>>(new Map());

  // Rust-side opaque cache
  const wrappedCache = useMemo(() => new sched.WrappedTemplateCache(), []);

  const fetchEvents = useCallback(async (templateId: string): Promise<sched.WorkflowTemplateEvent[]> => {
    // Return cached if available
    const cached = eventsCache.current.get(templateId);
    if (cached) return cached;

    // Deduplicate in-flight requests
    const existing = inflight.current.get(templateId);
    if (existing) return existing;

    if (!client || !token) {
      throw new Error('Not authenticated');
    }

    const promise = (async () => {
      const response = await sched.WrappedApiClient.list_workflow_template_events(client, token, templateId);
      const events = response.events;
      eventsCache.current.set(templateId, events);
      inflight.current.delete(templateId);
      return events;
    })();

    inflight.current.set(templateId, promise);
    return promise;
  }, [client, token]);

  const getTemplateState = useCallback(async (
    templateId: string,
    version?: number,
  ): Promise<sched.WorkflowTemplateState> => {
    const allEvents = await fetchEvents(templateId);
    const eventsSlice = version != null ? allEvents.slice(0, version) : allEvents;
    const state = sched.build_workflow_template_state(eventsSlice);

    // Feed the version-appropriate state into the Rust-side cache.
    // The database adapter uses this to expand workflow instances, so it must
    // match the version the instance is locked to (not always latest).
    //
    // Limitation: the cache is keyed by template ID alone. If multiple instances
    // pin different versions of the same template, the last resolved version wins.
    // A future fix would key by (templateId, version) on the Rust side.
    wrappedCache.set(templateId, state);

    // Sync the Rust-side DataProvider template cache so the fuzzy schedule
    // includes workflow instance steps. This triggers a schedule rebuild.
    if (safeData) {
      sched.DataManager.sync_template_cache(safeData, wrappedCache).catch((e: any) =>
        console.error('Failed to sync template cache:', e),
      );
    }

    return state;
  }, [fetchEvents, wrappedCache, safeData]);

  const value = useMemo<TemplateCache>(() => ({
    getTemplateState,
    wrappedCache,
  }), [getTemplateState, wrappedCache]);

  return (
    <TemplateCacheContext.Provider value={value}>
      {children}
    </TemplateCacheContext.Provider>
  );
}
